<template>
  <modal :open="internalOpen" :width="1400">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t('pages.campaigns.driverList.modal.specialDriverCheck.title') }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <BusyArea :busy="loadingRegistrations">
          <div class="flex flex-col w-full">
            <div class="flex flex-col gap-y-5">
              <div class="grid grid-cols-6 gap-y-4 gap-x-2">
                <div>
                  <span class="text-sm font-bold">
                    {{ $t('region') }}
                  </span>
                </div>
                <div>
                  <span class="text-sm font-bold">
                    {{ $t('campaignRegistration.driver') }}
                  </span>
                </div>
                <div>
                  <span class="text-sm font-bold">
                    {{ $t('campaignRegistration.specialDriverAssignment.task') }}
                  </span>
                </div>
                <div class="flex flex-col">
                  <span class="text-sm font-bold">
                    {{ $t('campaignRegistration.specialDriverAssignment.completed') }}
                  </span>
                  <span class="text-xs">
                    {{ $t('campaignRegistration.specialDriverAssignment.completedHelpText') }}
                  </span>
                </div>
                <div class="flex flex-col">
                  <span class="text-sm font-bold">
                    {{ $t('campaignRegistration.specialDriverAssignment.specialDriverPhoto') }}
                  </span>
                  <span class="text-xs">
                    {{ $t('campaignRegistration.specialDriverAssignment.specialDriverHelpTextPhoto') }}
                  </span>
                </div>
                <div class="flex flex-col">
                  <span class="text-sm font-bold">
                    {{ $t('campaignRegistration.specialDriverAssignment.specialDriverStanding') }}
                  </span>
                  <span class="text-xs">
                    {{ $t('campaignRegistration.specialDriverAssignment.specialDriverHelpTextStanding') }}
                  </span>
                </div>
                <template v-if="!loadingRegistrations">
                  <template v-for="row in data" :key="row.campaignRegistration['@id']">
                    <div>
                      {{ row.campaignRegistration.driver.region?.name}}
                    </div>
                    <div>
                      {{ row.campaignRegistration.driver.user.firstName }}
                      {{ row.campaignRegistration.driver.user.lastName }}
                    </div>
                    <div>
                      {{ $t(`campaignRegistration.specialDriverAssignment.type.${row.assignment.type}`) }}
                    </div>
                    <div>
                      <switch-input
                        v-model="row.assignment.completed"
                        :value="`${row.campaignRegistration['@id']}.completed`"
                      />
                    </div>
                    <div>
                      <switch-input
                        v-model="row.campaignRegistration.driver.specialDriverPhoto"
                        :value="`${row.campaignRegistration['@id']}.specialDriverPhoto`"
                      />
                    </div>
                    <div>
                      <switch-input
                        v-model="row.campaignRegistration.driver.specialDriverStanding"
                        :value="`${row.campaignRegistration['@id']}.specialDriverStanding`"
                      />
                    </div>
                  </template>
                </template>
              </div>
            </div>
            <div class="w-full text-center">
              <custom-button
                variant="primary"
                class="mt-15"
                :busy="loading"
                @click="save"
              >
                {{ $t('save') }}
              </custom-button>
            </div>
          </div>
        </BusyArea>
      </div>
    </form>
  </modal>
</template>
<script setup lang="ts">
import Modal from '@utils/Modal/Modal.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import CustomButton from '@utils/CustomButton.vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import { useProvider } from '@utils/table/hooks/provider';
import { computed, inject, type Ref, ref, watch } from 'vue';
import SwitchInput from '@utils/inputs/Switch.vue';
import ExclamationIcon from '@/assets/images/exclamation.svg?component';
import type {
  CampaignGroup, CampaignRegistration, HydraCollection, SpecialDriverAssignment,
} from '@/entities/Entities';
import BusyArea from '@utils/busy/BusyArea.vue';
import globalFilterKey from '@utils/globalFilter/injects';

interface Props {
  open: boolean;
  campaignGroup: CampaignGroup;
}

interface EditableCheckData {
  campaignRegistration: CampaignRegistration;
  assignment: SpecialDriverAssignment;
}

const emit = defineEmits(['update:open', 'saved']);

const globalFilter = inject(globalFilterKey) as Ref<{ regions: {label: string, value: string}[] }>;



const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const { t } = useI18n();
const notify = useNotify();
const [exec, { loading }] = useFetchAction();
const data = ref<Array<EditableCheckData>>([]);

const closeModal = () => {
  internalOpen.value = false;
};

const filterParams = computed(() => ({
  filter: {
    'campaign.group': props.campaignGroup?.id,
    'specialDriverAssignment[gte]': '1',
    'inArray[campaign.region]': globalFilter.value?.regions,
  },
  pagination: false as const,
}));

const {
  data: registrationsWithSpecialDriverData,
  loading: loadingRegistrations,
  refetch,
} = useProvider<HydraCollection<CampaignRegistration>>(
  '/campaign_registrations',
  filterParams,
  {
    disabled: computed(() => !internalOpen.value),
  },
);


const registrationsWithSpecialDriver = computed(() => registrationsWithSpecialDriverData.value?.['hydra:member']);

const initialData = () => {
  const checkData: EditableCheckData[] = [];
  registrationsWithSpecialDriver.value?.forEach((registration) => {
    registration.specialDriverAssignments?.forEach((assignment) => {
      checkData.push({
        campaignRegistration: registration,
        assignment: { ...assignment },
      });
    });
  });
  return checkData;
};

watch(loadingRegistrations, (value: boolean) => {
  if (!value) {
    data.value = initialData();
  }
});

const save = async () => {
  try {
    await exec('/special_driver_assignments/check_assignments', {
      method: 'POST',
      body: {
        data: data.value.map((item) => ({
          id: item.assignment.id,
          completed: item.assignment.completed,
          specialDriverPhoto: item.campaignRegistration.driver.specialDriverPhoto,
          specialDriverStanding: item.campaignRegistration.driver.specialDriverStanding,
        })),
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.campaigns.driverList.modal.new.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    console.log(e);
    notify?.({
      variant: 'error',
      title: t('pages.campaigns.driverList.modal.new.saveError'),
    });
  }
};

</script>
